import { Input } from "@/components/ui/input";
import { cn } from "@repo/design-system/utils";
import {
  useEffect,
  useId,
  useMemo,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent
} from "react";

type InputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "role" | "type"
>;

export interface ComboboxProps extends InputProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Suggestions, shown filtered by what has been typed. The field stays free text. */
  options: readonly string[];
  /** Accessible name of the suggestion list. */
  listLabel: string;
  /** Extra classes on every option's text, e.g. a session-replay mask class. */
  optionClassName?: string;
}

/**
 * A free-text field with a list of suggestions (the ARIA 1.2 combobox with a
 * listbox popup): typing filters the suggestions but never restricts the
 * value, and the input keeps focus the whole time - the list is reached with
 * the arrow keys, not by moving focus into it.
 *
 * Built from the storefront's own `Input` and styled like its `Popover`, so it
 * matches every other field in a dialog (Medusa UI's `Input` is 32px tall
 * against these 40px fields). The list renders inline under the field, not in
 * a portal: inside a Radix dialog a portaled list would count as an outside
 * click and sit under the dialog's `pointer-events: none` body.
 *
 * Keys: ArrowDown/ArrowUp open the list and move through it, Enter picks the
 * highlighted suggestion (with none highlighted Enter submits the form as
 * usual), Escape closes the list without closing a surrounding dialog, Tab
 * leaves.
 */
export function Combobox({
  value,
  onValueChange,
  options,
  listLabel,
  optionClassName,
  onFocus,
  onBlur,
  onKeyDown,
  ...inputProps
}: ComboboxProps) {
  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;

  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);

  const filtered = useMemo(() => {
    const needle = value.trim().toLowerCase();
    return options.filter((option) => option.toLowerCase().includes(needle));
  }, [options, value]);

  // Nothing to pick when the only match is exactly what is already typed.
  const visible = useMemo(
    () =>
      filtered.length === 1 &&
      filtered[0].toLowerCase() === value.trim().toLowerCase()
        ? []
        : filtered,
    [filtered, value]
  );

  const open = focused && !dismissed && visible.length > 0;
  // `options` can shrink under an open list (a refetch), so the highlight is
  // clamped to what is on screen rather than trusted.
  const activeIndex = open && highlighted < visible.length ? highlighted : -1;

  // Keep the highlighted row visible in a scrolling list.
  useEffect(() => {
    if (activeIndex < 0) return;
    const row = document.getElementById(`${listId}-option-${activeIndex}`);
    if (typeof row?.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, listId]);

  // Radix Dialog closes on Escape from a capture listener on `document`, which
  // runs before any React handler. A capture listener on `window` runs first,
  // so while the list is open it can take the key for itself.
  useEffect(() => {
    if (!open) return;
    const closeListOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setDismissed(true);
    };
    window.addEventListener("keydown", closeListOnEscape, true);
    return () => window.removeEventListener("keydown", closeListOnEscape, true);
  }, [open]);

  const select = (option: string) => {
    onValueChange(option);
    setDismissed(true);
    setHighlighted(-1);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (visible.length === 0) return;
      event.preventDefault();
      if (!open) {
        setDismissed(false);
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setHighlighted(
        activeIndex === -1
          ? step === 1
            ? 0
            : visible.length - 1
          : (activeIndex + step + visible.length) % visible.length
      );
    } else if (event.key === "Enter" && open && activeIndex >= 0) {
      event.preventDefault();
      select(visible[activeIndex]);
    }
  };

  return (
    <div className="relative">
      <Input
        {...inputProps}
        value={value}
        onChange={(event) => {
          setDismissed(false);
          setHighlighted(-1);
          onValueChange(event.target.value);
        }}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          setDismissed(false);
          onBlur?.(event);
        }}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 ? optionId(activeIndex) : undefined
        }
      />
      <ul
        id={listId}
        role="listbox"
        aria-label={listLabel}
        hidden={!open}
        // Keep focus in the input: a mousedown anywhere in the list (a row,
        // its padding, the scrollbar) would otherwise blur it and close the list.
        onMouseDown={(event) => event.preventDefault()}
        className="bg-popover text-popover-foreground absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border p-1 shadow-md"
      >
        {open
          ? visible.map((option, index) => (
              <li
                key={option}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                onMouseMove={() => setHighlighted(index)}
                onClick={() => select(option)}
                className={cn(
                  "cursor-pointer rounded-sm px-2 py-1.5 text-sm",
                  index === activeIndex && "bg-accent text-accent-foreground"
                )}
              >
                <span className={cn("block truncate", optionClassName)}>
                  {option}
                </span>
              </li>
            ))
          : null}
      </ul>
    </div>
  );
}
